// The lesson-note bound, and the counter under the note box.
//
// The status taxonomy is `core/src/attendance.rs` (#214), reached through
// `coreSync()`. The bound and the counter's numbers are here.
//
// ── Why NOTE_BODY_MAX is not in the core (#225) ─────────────────────────────
//
// The same 65535 is `STUDENT_NOTE_MAX` and `FAMILY_NOTES_MAX` in `people.js`
// and `TEMPLATE_BODY_MAX` in `noteTemplates.js`, with `firestore.rules`
// canonical — the only enforcement a client cannot bypass. This copy fails
// fast with a sentence a teacher can read. It is a module-scope constant, and
// `coreSync()` throws until `loadCore()` has resolved, so a core-backed value
// would have to be a call at every consumer.
//
// `test/domain/lessonNotes.test.js` pins it: it reads the three
// `optionalText(d.notes, …)` clauses out of `validAttendanceNotes` in
// `firestore.rules` — slicing that function out first, because `65536`
// appears elsewhere in the file — asserts this constant is one under each, and
// asserts the attendance collection calls that helper. The same file pins
// `STUDENT_NOTE_MAX` to this constant; `FAMILY_NOTES_MAX` and
// `TEMPLATE_BODY_MAX` are pinned by their own files' tests.

// The three lesson-note bodies live on the attendance document, one per
// audience, and each is bounded at 65536 by firestore.rules — the same bound a
// family's `notes` and a note template's `body` get.
//
// ── Why the number is so large (issues #82, #93) ────────────────────────────
//
// MMS applies no length limit to a lesson note, and its editor's character
// counter is hidden. The bound sits far above anything a teacher would type.
//
// ── Why there is a bound ───────────────────────────────────────────────────
//
//   * three bodies share one attendance document;
//   * the phone lists the whole attendance collection and Firestore persists
//     it, so every device caches every body — Rules.kt maps all three, and
//     StudioRepository's ENTRY_LIMIT lists 5,000 documents. The bound is what
//     makes that cache *finite* (#82): 5,000 lessons × 3 bodies at 65,535 is
//     ~2.9 GB;
//   * Firestore's 1 MiB document ceiling is otherwise the only stop, and
//     hitting *that* fails the whole writeBatch: the charge, the credit, the
//     package consume and the payment go down with the note.
//
// ── The arithmetic, in bytes, because that is what Firestore counts ─────────
//
// The ceiling is 1,048,576 bytes and it counts the document path, every field
// name, every field value and 32 bytes of overhead. Worst case is four-byte
// UTF-8 throughout:
//
//     3 bodies × 65,535 characters × 4 bytes  =  786,420 bytes
//     everything else on the document         <    4,096 bytes  (reserved)
//     ────────────────────────────────────────────────────────
//     worst case                                 790,516 bytes  =  0.754 MiB
//
// which leaves 258,060 bytes — a quarter of the ceiling — spare. "Everything
// else" is the document path, occurrenceId, studentId, familyId, status,
// occurredOn, updatedAt, updatedBy, the three sub-field names and the 32 bytes,
// well under 1 KiB; 4 KiB leaves room for more fields.
//
// Two conservatisms in that sum, both pointing the safe way:
//
//   * `maxLength` and `String.slice` count UTF-16 code units, and a character
//     costing four bytes in UTF-8 is *two* of those — so four bytes per unit
//     over-counts by double. The true worst case per unit is three bytes, a BMP
//     character, which would put the sum at 589,815 bytes.
//   * firestore.rules' `size()` counts characters, not bytes, and a string
//     never holds more characters than UTF-16 units — so a clamp at 65,535
//     units always satisfies `size() < 65536`. attendanceRules.test.jsx writes
//     a body of multi-byte characters at exactly the clamp length, and fails
//     if `size()` counts bytes.
//
// 65535 and not 65536 because the rule is written `size() < 65536` to match its
// neighbours in that file, so the longest string it accepts is one shorter than
// the bound. Same convention as TEMPLATE_BODY_MAX.
//
// The client stops here in two places: the textarea's `maxLength` in
// LessonNotesTab.jsx, and the clamp in useAttendanceForm's setNote — the
// second because applying a template *appends*, so the box's own maxLength does
// not see it. The attendance write is a writeBatch carrying the charge, the
// credit and any payment, so one refused field would discard the whole lesson
// mark.
import { coreSync } from './core.js';

export const NOTE_BODY_MAX = 65535;

// What the counter under the note box reads, or null for "say nothing".
//
// Here rather than in LessonNotesTab.jsx so it can be tested without a DOM.
//
// **Which state, and the words, are the core's** (`note_counter` and
// `note_counter_text` in `core/src/lesson.rs`), because a phone's note box
// needs the same counter. The two numbers are written by `toLocaleString()`
// here and handed in, because `toLocaleString()` is ICU and its output can
// change with the runtime — the reason `money.format` is kept out too.
//
// Showing a counter at all differs from MMS, which has one and hides it; the
// call site says why.
//
//   * An empty box says nothing.
//   * An ordinary note gets a running figure with **no limit attached**.
//   * The last thousand characters get the limit too.
//   * A full note gets a sentence rather than a number, and the sentence names
//     the template case on purpose: applyTemplate appends and setNote clamps, so
//     a template applied to a full note adds nothing, and without the sentence
//     that happens with no indication at all (#93).
//
// The two numbers are grouped as the studio's Region groups them (`locale`,
// #534) — `65,535` in the United States, `65.535` in Germany — not as the
// *browser's* locale. A caller with no Region passes nothing and gets the
// browser's.
export function noteCounterText(body, max, locale = undefined) {
  const c = coreSync().noteCounter(String(body ?? ''), max);
  if (!c) return null;
  const n = (v) => {
    try { return v.toLocaleString(locale); } catch { return v.toLocaleString(); }
  };
  return coreSync().noteCounterText(c.kind, n(c.used), n(c.max));
}
